import {fetchAPI} from '@/services/api';
import {
    buildSeataTxHeaders,
    clearSeataTxXid,
    getSeataTxXid,
    setActiveSeataFlow,
    setSeataTxXid,
    type SeataWizardFlow,
} from '@/services/seataTxHolder';

export interface SeataGlobalTxVO {
    xid: string;
}

function txBase(apiBase: string): string {
    return `${apiBase}/seata/tx`;
}

/**
 * Begin a wizard global transaction on the given microservice (audit / account).
 */
export async function beginWizardTx(
    flow: SeataWizardFlow,
    apiBase: string,
    txName: string,
): Promise<string> {
    setActiveSeataFlow(flow);
    const params = new URLSearchParams({txName});
    const result = await fetchAPI<SeataGlobalTxVO>(`${txBase(apiBase)}/begin?${params.toString()}`, {
        method: 'POST',
    });
    const xid = result?.xid;
    if (!xid) {
        throw new Error('Failed to begin transaction');
    }
    setSeataTxXid(flow, xid);
    return xid;
}

/** Begin if not already started for this flow. */
export async function ensureWizardTxStarted(
    flow: SeataWizardFlow,
    apiBase: string,
    txName: string,
): Promise<string> {
    const existing = getSeataTxXid(flow);
    if (existing) {
        setActiveSeataFlow(flow);
        return existing;
    }
    return beginWizardTx(flow, apiBase, txName);
}

export async function commitWizardTx(
    flow: SeataWizardFlow,
    apiBase: string,
    xid?: string | null,
): Promise<void> {
    const txXid = xid ?? getSeataTxXid(flow);
    // WHY: silent return left AT branches uncommitted → Seata timeout undo wiped IC transfers
    if (!txXid) {
        throw new Error(`Failed to commit wizard TX: missing xid for flow=${flow}`);
    }
    setActiveSeataFlow(flow);
    setSeataTxXid(flow, txXid);
    await fetchAPI<boolean>(`${txBase(apiBase)}/commit`, {
        method: 'POST',
        headers: buildSeataTxHeaders(),
    });
    clearSeataTxXid(flow);
}

export async function rollbackWizardTx(
    flow: SeataWizardFlow,
    apiBase: string,
    xid?: string | null,
): Promise<void> {
    const txXid = xid ?? getSeataTxXid(flow);
    if (!txXid) return;
    setActiveSeataFlow(flow);
    setSeataTxXid(flow, txXid);
    try {
        await fetchAPI<boolean>(`${txBase(apiBase)}/rollback`, {
            method: 'POST',
            headers: buildSeataTxHeaders(),
        });
    } finally {
        clearSeataTxXid(flow);
    }
}

export function clearWizardTxLocal(flow: SeataWizardFlow): void {
    clearSeataTxXid(flow);
    if (getSeataTxXid() === null) {
        setActiveSeataFlow(null);
    }
}
